import React, { useEffect, useState } from 'react';
import { Button, Select, Snackbar, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { createPreSignedUrl, getImagesList } from '../../api/files';
import ListPicturesComponent from './components/ListPicturesComponent/index';
import MenuItem from '@mui/material/MenuItem';
import AddPhotoAlternateTwoToneIcon from '@mui/icons-material/AddPhotoAlternateTwoTone';
import MuiAlert, { AlertColor } from '@mui/material/Alert';

export type ImageType = {
	id: string
	isPaid: boolean
	isPublic: boolean
	isSystem: boolean
	name: string
	type: 'USER_PICTURE' | 'GROUP_COVER' | 'LEAGUE_LOGO' | 'LEVEL_ICON'
	url: string
};

const sortByOrder = [
	{
		id: 'asc',
		label: 'Ascending (smallest value first)'
	},
	{
		id: 'desc',
		label: 'Descending (largest value first)'
	}
];

const sortByValue = [
	{
		id: 'name',
		label: 'Name'
	},
	{
		id: 'isPaid',
		label: 'Is paid'
	},
	{
		id: 'price',
		label: 'Price'
	},
	{
		id: 'isPublic',
		label: 'Is public'
	},
	{
		id: 'createdDate',
		label: 'Created date'
	}
];

const MediaLibrary = () => {
	const [alignment, setAlignment] = useState('LEAGUE_LOGO');
	const [activatedTab, setActivatedTab] =
		useState<'USER_PICTURE' | 'GROUP_COVER' | 'LEAGUE_LOGO' | 'LEVEL_ICON'>('LEAGUE_LOGO');
	const [pictureList, setPictureList] = useState<ImageType[] | []>([]);
	const [sortBy, setSortBy] = useState(sortByValue[0].id);
	const [sortOrder, setSortOrder] = useState(sortByOrder[0].id);
	const [hasMore, setHasMore] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
		isOpen: false,
		alertType: 'success',
		message: ''
	});
	
	useEffect(() => {
		(async () => {
			setPictureList(await getImagesList({ type: activatedTab, sortBy, orderDirection: sortOrder }));
			setHasMore(true);
		})();
	}, [activatedTab]);
	
	useEffect(() => {
		(async () => {
			setPictureList(await getImagesList({ type: activatedTab, sortBy, orderDirection: sortOrder }));
			setHasMore(true);
		})();
	}, [sortBy, sortOrder]);
	
	const handleChangeAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
		setAlignment(newAlignment);
	};
	
	const getMoreImage = async () => {
		const params = {
			cursor: pictureList[pictureList.length - 1].id,
			orderDirection: sortOrder,
			type: activatedTab,
			sortBy
		};
		
		const newImageList = await getImagesList(params);
		setPictureList([...pictureList, ...newImageList]);
		if (!newImageList.length) setHasMore(false);
		setIsLoading(false);
	};
	
	const handleUploadImage = async (files: any) => {
		try {
			const preSignedImage = await createPreSignedUrl(
				{ type: activatedTab, name: files[0].name, isPublic: true}
			)
			await fetch(preSignedImage.presignedUrl, {
				method: "PUT",
				body: files[0],
			});
			setOpenSnackbar({ isOpen: true, message: 'Image has been successfully uploaded', alertType: 'success'});
			setPictureList(await getImagesList({ type: activatedTab, sortBy, orderDirection: sortOrder }));
		} catch (e) {
			setOpenSnackbar({ isOpen: true, message: 'Something went wrong, please try uploading the image later', alertType: 'error'});
		}
	};
	
	return (
		<div>
			<ToggleButtonGroup
				color="primary"
				key={alignment}
				value={alignment}
				defaultValue={'LEAGUE_LOGO'}
				defaultChecked={true}
				exclusive
				onChange={handleChangeAlignment}
				aria-label="Platform"
				className="mt-5"
			>
				<ToggleButton onClick={() => setActivatedTab('LEAGUE_LOGO')} value="LEAGUE_LOGO">League pictures</ToggleButton>
				<ToggleButton onClick={() => setActivatedTab('LEVEL_ICON')} value="LEVEL_ICON">Level pictures</ToggleButton>
				<ToggleButton onClick={() => setActivatedTab('GROUP_COVER')} value="GROUP_COVER">Group pictures</ToggleButton>
				<ToggleButton onClick={() => setActivatedTab('USER_PICTURE')} value="USER_PICTURE">User pictures</ToggleButton>
			</ToggleButtonGroup>
			<div className="flex items-center justify-end mb-3 border-b-[1px] border-solid border-[#808080] pb-3 mr-5 mt-5">
				<div>
					<label id="sortBy" className="mr-2 font-bold">Sort by value</label>
					<Select
						labelId="sortBy"
						id="sort-by-select"
						value={sortBy}
						onChange={(event) => setSortBy(event.target.value)}
					>
						{sortByValue.map((value, index) => {
							return <MenuItem value={value.id} key={index.toString()}>{value.label}</MenuItem>
						})}
					</Select>
				</div>
				<div className="ml-10">
					<label id="sortByOrder" className="mr-2 font-bold">Sorting order</label>
					<Select
						labelId="sortByOrder"
						id="sort-by-order-select"
						value={sortOrder}
						onChange={(event) => setSortOrder(event.target.value)}
					>
						{sortByOrder.map((value, index) => {
							return <MenuItem value={value.id} key={index.toString()}>{value.label}</MenuItem>
						})}
					</Select>
				</div>
			</div>
			<ListPicturesComponent
				list={pictureList}
				activatedTab={activatedTab}
				hasMore={hasMore}
				isLoading={isLoading}
				setIsLoading={setIsLoading}
				setList={(value) => setPictureList(value)}
				getMoreImage={getMoreImage}
			/>
			<Button variant="contained" className="flex items-center">
				<label
					htmlFor="active-button-file"
				>
					<AddPhotoAlternateTwoToneIcon className="mr-5" /> Upload new image
				</label>
				<input
					accept="image/*"
					style={{ display: "none" }}
					id="active-button-file"
					multiple
					type="file"
					onChange={(event) => handleUploadImage(event.target.files)}
				/>
			</Button>
			<Snackbar
				open={openSnackbar.isOpen}
				autoHideDuration={5000}
				onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
			>
				<MuiAlert
					onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
					severity={openSnackbar.alertType}
					sx={{ width: '100%' }}
				>
					{openSnackbar.message}
				</MuiAlert>
			</Snackbar>
		</div>
	)
};

export default MediaLibrary;